import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  type GetCourseVersionHistoryQuery,
  useGetCourseVersionHistoryQuery,
  useGetSnapshotDiffQuery,
} from "@axio/graphql/generated/graphql";

import VersionHistorySheet from "./VersionHistorySheet";

type SnapshotVersionEntry =
  GetCourseVersionHistoryQuery["getCourseVersionHistory"]["groups"][number]["snapshots"][number];
type VersionHistoryStatus = "loading" | "error" | "empty" | "ready";

interface VersionHistorySheetContainerProps {
  open: boolean;
  courseId: string;
  courseName: string;
  onClose: () => void;
}

function getAdjacentOlderSnapshot(entries: SnapshotVersionEntry[], snapshotId: string): SnapshotVersionEntry | null {
  const selectedIndex = entries.findIndex((entry) => entry.id === snapshotId);
  if (selectedIndex < 0) {
    return null;
  }

  return entries[selectedIndex + 1] ?? null;
}

function getAdjacentNewerSnapshot(entries: SnapshotVersionEntry[], snapshotId: string): SnapshotVersionEntry | null {
  const selectedIndex = entries.findIndex((entry) => entry.id === snapshotId);
  if (selectedIndex <= 0) {
    return null;
  }

  return entries[selectedIndex - 1] ?? null;
}

function VersionHistorySheetContainer({ open, courseId, courseName, onClose }: VersionHistorySheetContainerProps) {
  const [selectedFromSnapshotId, setSelectedFromSnapshotId] = useState<string | null>(null);
  const [selectedToSnapshotId, setSelectedToSnapshotId] = useState<string | null>(null);

  const {
    data: versionHistoryData,
    loading: isVersionHistoryLoading,
    error: versionHistoryError,
  } = useGetCourseVersionHistoryQuery({
    variables: { courseId },
    skip: !open,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const versionHistory = versionHistoryData?.getCourseVersionHistory;
  const snapshots = useMemo(
    () => (versionHistory?.success ? versionHistory.groups.flatMap((group) => group.snapshots) : []),
    [versionHistory]
  );
  const currentVersion = useMemo(() => snapshots.find((snapshot) => snapshot.isCurrent) ?? null, [snapshots]);
  const selectedFromSnapshot = useMemo(
    () => snapshots.find((snapshot) => snapshot.id === selectedFromSnapshotId) ?? null,
    [selectedFromSnapshotId, snapshots]
  );
  const selectedToSnapshot = useMemo(
    () => snapshots.find((snapshot) => snapshot.id === selectedToSnapshotId) ?? null,
    [selectedToSnapshotId, snapshots]
  );
  const hasCurrentVersionContractError = Boolean(versionHistory?.success && snapshots.length > 0 && !currentVersion);

  const historyStatus: VersionHistoryStatus = versionHistoryError
    ? "error"
    : !versionHistory
      ? isVersionHistoryLoading
        ? "loading"
        : "empty"
      : !versionHistory.success || hasCurrentVersionContractError
        ? "error"
        : snapshots.length === 0
          ? "empty"
          : "ready";

  const historyErrorMessage = versionHistoryError
    ? "Unable to load version history."
    : hasCurrentVersionContractError
      ? "Version history did not include a current snapshot."
      : versionHistory && !versionHistory.success
        ? "Version history request was unsuccessful."
        : undefined;

  const setOrderedComparison = useCallback(
    (snapshotIdA: string, snapshotIdB: string) => {
      if (snapshotIdA === snapshotIdB) {
        return;
      }

      const snapshotA = snapshots.find((snapshot) => snapshot.id === snapshotIdA);
      const snapshotB = snapshots.find((snapshot) => snapshot.id === snapshotIdB);
      if (!snapshotA || !snapshotB) {
        return;
      }

      if (snapshotA.versionNumber <= snapshotB.versionNumber) {
        setSelectedFromSnapshotId(snapshotA.id);
        setSelectedToSnapshotId(snapshotB.id);
        return;
      }

      setSelectedFromSnapshotId(snapshotB.id);
      setSelectedToSnapshotId(snapshotA.id);
    },
    [snapshots]
  );

  useEffect(() => {
    if (!open) {
      setSelectedFromSnapshotId(null);
      setSelectedToSnapshotId(null);
      return;
    }

    if (historyStatus !== "ready" || !currentVersion) {
      return;
    }

    const selectedToSnapshotExists = selectedToSnapshotId
      ? snapshots.some((snapshot) => snapshot.id === selectedToSnapshotId)
      : false;

    if (selectedToSnapshotExists) {
      return;
    }

    const adjacentOlderSnapshot = getAdjacentOlderSnapshot(snapshots, currentVersion.id);
    setSelectedToSnapshotId(currentVersion.id);
    setSelectedFromSnapshotId(adjacentOlderSnapshot?.id ?? null);
  }, [currentVersion, historyStatus, open, selectedToSnapshotId, snapshots]);

  useEffect(() => {
    if (!open || historyStatus !== "ready" || !selectedToSnapshot || selectedFromSnapshot) {
      return;
    }

    const adjacentOlderSnapshot = getAdjacentOlderSnapshot(snapshots, selectedToSnapshot.id);
    if (adjacentOlderSnapshot) {
      setSelectedFromSnapshotId(adjacentOlderSnapshot.id);
      return;
    }

    const adjacentNewerSnapshot = getAdjacentNewerSnapshot(snapshots, selectedToSnapshot.id);
    if (adjacentNewerSnapshot) {
      setOrderedComparison(selectedToSnapshot.id, adjacentNewerSnapshot.id);
    }
  }, [historyStatus, open, selectedFromSnapshot, selectedToSnapshot, setOrderedComparison, snapshots]);

  const handleSelectSnapshotRow = useCallback(
    (snapshotId: string) => {
      const selectedSnapshot = snapshots.find((snapshot) => snapshot.id === snapshotId);
      if (!selectedSnapshot) {
        return;
      }

      const adjacentOlderSnapshot = getAdjacentOlderSnapshot(snapshots, selectedSnapshot.id);
      if (adjacentOlderSnapshot) {
        setSelectedToSnapshotId(selectedSnapshot.id);
        setSelectedFromSnapshotId(adjacentOlderSnapshot.id);
        return;
      }

      const adjacentNewerSnapshot = getAdjacentNewerSnapshot(snapshots, selectedSnapshot.id);
      if (adjacentNewerSnapshot) {
        setOrderedComparison(selectedSnapshot.id, adjacentNewerSnapshot.id);
        return;
      }

      setSelectedToSnapshotId(selectedSnapshot.id);
      setSelectedFromSnapshotId(null);
    },
    [setOrderedComparison, snapshots]
  );

  const handleSelectFromSnapshot = useCallback(
    (snapshotId: string) => {
      if (!selectedToSnapshot) {
        return;
      }

      setOrderedComparison(snapshotId, selectedToSnapshot.id);
    },
    [selectedToSnapshot, setOrderedComparison]
  );

  const handleSelectToSnapshot = useCallback(
    (snapshotId: string) => {
      if (!selectedFromSnapshot) {
        setSelectedToSnapshotId(snapshotId);
        return;
      }

      setOrderedComparison(selectedFromSnapshot.id, snapshotId);
    },
    [selectedFromSnapshot, setOrderedComparison]
  );

  const shouldLoadDiff =
    open &&
    historyStatus === "ready" &&
    selectedFromSnapshot !== null &&
    selectedToSnapshot !== null &&
    selectedFromSnapshot.id !== selectedToSnapshot.id;
  const {
    data: diffData,
    loading: isDiffLoading,
    error: diffError,
  } = useGetSnapshotDiffQuery({
    variables: {
      snapshotIdA: selectedFromSnapshot?.id ?? "",
      snapshotIdB: selectedToSnapshot?.id ?? "",
    },
    skip: !shouldLoadDiff,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const snapshotDiff = diffData?.getSnapshotDiff ?? null;
  const diffErrorMessage = diffError
    ? "Unable to load snapshot diff."
    : snapshotDiff && !snapshotDiff.success
      ? "Snapshot diff request was unsuccessful."
      : undefined;

  return (
    <VersionHistorySheet
      open={open}
      courseName={courseName}
      versionGroups={historyStatus === "ready" && versionHistory?.success ? versionHistory.groups : []}
      currentVersion={historyStatus === "ready" ? currentVersion : null}
      selectedFromSnapshot={historyStatus === "ready" ? selectedFromSnapshot : null}
      selectedToSnapshot={historyStatus === "ready" ? selectedToSnapshot : null}
      diff={snapshotDiff?.success ? snapshotDiff : null}
      historyStatus={historyStatus}
      historyErrorMessage={historyErrorMessage}
      isDiffLoading={shouldLoadDiff && isDiffLoading}
      diffErrorMessage={diffErrorMessage}
      onClose={onClose}
      onSelectSnapshotRow={handleSelectSnapshotRow}
      onSelectFromSnapshot={handleSelectFromSnapshot}
      onSelectToSnapshot={handleSelectToSnapshot}
    />
  );
}

export default VersionHistorySheetContainer;
